import { useEffect, useState, useCallback } from "react";
import { Bike, Loader2, Check, ChevronDown } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fcfa, formatDate } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { LivreurFund } from "../../components/admin/LivreurFund";

export default function Livreurs() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [openName, setOpenName] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/livreurs").then(({ data }) => setRows(data)).catch(() => setRows(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const pay = async (name) => {
    if (!window.confirm(`Marquer comme payé tout ce qui est dû à ${name} ?`)) return;
    setBusy(name);
    try {
      const { data } = await api.post("/admin/livreurs/pay", { name });
      toast.success(`${data.marked_paid} livraison(s) réglée(s)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (rows === null) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;
  if (rows === false) return <p className="text-sm text-red-500">Impossible de charger les livreurs.</p>;

  const totalToPay = rows.reduce((s, r) => s + r.to_pay, 0);

  return (
    <div className="space-y-6" data-testid="livreurs-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink flex items-center gap-2">
          <Bike className="w-7 h-7 text-brand" /> Livreurs
        </h1>
        <p className="text-brand-muted mt-1">Règlement des livreurs — total à payer : <b className="text-green-700">{fcfa(totalToPay)}</b></p>
      </div>

      <LivreurFund />

      {rows.length === 0 ? (
        <p className="text-brand-muted bg-white border border-brand-line rounded-xl p-8 text-center">
          Aucune livraison enregistrée avec un livreur. Renseignez un livreur dans <b>Livraisons</b> en marquant une commande « Livré ».
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.name} data-testid={`livreur-card-${r.name}`} className="bg-white border border-brand-line rounded-2xl overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 p-5">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-green-100 text-green-700 flex items-center justify-center"><Bike className="w-5 h-5" /></span>
                  <div>
                    <p className="font-heading font-bold text-brand-ink">{r.name}</p>
                    <p className="text-xs text-brand-muted">{r.deliveries} livraison(s) · déjà payé {fcfa(r.paid)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-[11px] text-brand-muted">À payer</p>
                    <p className="font-heading font-black text-green-700">{fcfa(r.to_pay)}</p>
                  </div>
                  {(user?.role === "patron" || user?.role === "vendeur") && (
                    <button onClick={() => pay(r.name)} disabled={r.to_pay <= 0 || busy === r.name} data-testid={`pay-livreur-${r.name}`}
                      className="bg-brand text-white hover:bg-brand-hover rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
                      {busy === r.name ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Marquer payé
                    </button>
                  )}
                </div>
              </div>

              <button onClick={() => setOpenName((n) => (n === r.name ? null : r.name))} data-testid={`livreur-history-toggle-${r.name}`}
                className="w-full flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold text-brand hover:bg-brand-cream/50 border-t border-brand-line">
                Détail des livraisons <ChevronDown className={`w-4 h-4 transition-transform ${openName === r.name ? "rotate-180" : ""}`} />
              </button>

              {openName === r.name && (
                <div className="px-5 pb-5" data-testid={`livreur-history-${r.name}`}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-brand-muted border-b border-brand-line">
                          <th className="py-2 pr-3 font-medium">Commande</th>
                          <th className="py-2 pr-3 font-medium">Client</th>
                          <th className="py-2 pr-3 font-medium">Date</th>
                          <th className="py-2 pr-3 font-medium text-right">Demandé</th>
                          <th className="py-2 pr-3 font-medium text-right">Payé au livreur</th>
                          <th className="py-2 font-medium text-right">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.items.map((it, i) => (
                          <tr key={i} className="border-b border-brand-line/50 last:border-0">
                            <td className="py-2 pr-3 font-medium text-brand-ink">{it.order_number}</td>
                            <td className="py-2 pr-3 text-brand-sub">{it.customer_name}</td>
                            <td className="py-2 pr-3 text-brand-muted">{formatDate(it.date)}</td>
                            <td className="py-2 pr-3 text-right text-brand-muted">{it.fee_proposed ? fcfa(it.fee_proposed) : "—"}</td>
                            <td className="py-2 pr-3 text-right font-semibold text-brand-ink">{fcfa(it.fee)}</td>
                            <td className="py-2 text-right">
                              <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${it.paid ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                                {it.paid ? (it.paid_by ? `payé par ${it.paid_by}` : "payé") : "à payer"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
